/* SCA — Shopify renders the cart. Public API: SCACart.open(), close(), refresh(). */
(() => {
  if (window.__scaCartDrawerManager) {
    window.__scaCartDrawerManager.mount();
    return;
  }
  let instance = null;

  function createDrawer(drawer) {
    const lifecycle = new AbortController();
    const signal = lifecycle.signal;
    const sectionId = drawer.dataset.sectionId;
    const cartUrl = drawer.dataset.cartUrl;
    const changeUrl = drawer.dataset.changeUrl;
    // Keep the current template and preview theme when rendering the drawer.
    const renderUrl = new URL(location.href);
    renderUrl.hash = '';
    renderUrl.searchParams.delete('sections');
    renderUrl.searchParams.delete('section_id');
    let sequence = Promise.resolve();
    let busy = false;
    let stale = false;
    let destroyed = false;
    let closeTimer;
    let frame;
    let returnFocus = null;
    let restoreFocus = null;
    let scrollStyles = null;
    const $ = selector => drawer.querySelector(selector);
    const on = (node, name, callback, options = {}) => node.addEventListener(name, callback, { ...options, signal });

    function setBusy(value) {
      busy = value;
      drawer.classList.toggle('is-busy', value);
      $('[data-cart-content]').setAttribute('aria-busy', String(value));
      drawer.querySelectorAll('[data-quantity], [data-sca-checkout]').forEach(button => { button.disabled = value || stale; });
      $('[data-cart-retry]').disabled = value;
      if (!value && restoreFocus && drawer.open) {
        const target = restoreFocus();
        (target || $('.sca-drawer__close')).focus({ preventScroll: true });
        restoreFocus = null;
      }
    }
    function errorMessage(message) {
      $('[data-cart-error-text]').textContent = message || '';
      $('[data-cart-error]').hidden = !message;
    }
    function updateCount(count) {
      const badge = $('[data-sca-drawer-count]');
      badge.textContent = count;
      badge.hidden = count === 0;
      document.querySelectorAll('[data-sca-header-cart-count]').forEach(el => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      document.querySelectorAll('.cart-count-bubble').forEach(el => {
        el.hidden = count === 0;
        const number = el.querySelector('[aria-hidden="true"]');
        if (number) number.textContent = count;
        const label = el.querySelector('.visually-hidden');
        if (label) label.textContent = count + (count === 1 ? ' article' : ' articles');
      });
      document.querySelectorAll('.tlph__icon-link').forEach(link => {
        if (new URL(link.href, location.href).pathname.replace(/\/$/, '') !== cartUrl.replace(/\/$/, '')) return;
        let badge = link.querySelector('.tlph__count');
        if (!badge && count > 0) {
          badge = document.createElement('span');
          badge.className = 'tlph__count';
          link.append(badge);
        }
        if (badge) { badge.textContent = count; badge.hidden = count === 0; }
      });
    }
    function applySection(html) {
      if (typeof html !== 'string' || !html.trim()) throw new Error('Le panier n’a pas pu être actualisé.');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const root = [...parsed.querySelectorAll('[data-sca-drawer]')].find(el => el.dataset.sectionId === sectionId);
      const next = root?.querySelector('[data-cart-content]');
      const count = Number(next?.dataset.count);
      if (!next || !Number.isInteger(count) || count < 0) throw new Error('Le panier n’a pas pu être actualisé.');
      const current = $('[data-cart-content]');
      const scrollTop = $('[data-cart-scroll]').scrollTop;
      const focused = document.activeElement;
      if (drawer.open && current.contains(focused)) {
        const key = focused.closest('[data-line-key]')?.dataset.lineKey;
        const action = focused.dataset.action;
        restoreFocus = () => {
          const row = [...drawer.querySelectorAll('[data-line-key]')].find(el => el.dataset.lineKey === key);
          return row?.querySelector('[data-action="' + (action || 'plus') + '"]')
            || $('[data-cart-discover]') || $('[data-quantity]');
        };
      }
      current.replaceWith(document.importNode(next, true));
      $('[data-cart-scroll]').scrollTop = scrollTop;
      updateCount(count);
      $('[data-cart-status]').textContent = 'Panier mis à jour : ' + count + (count === 1 ? ' article.' : ' articles.');
      stale = false;
    }
    async function loadSection() {
      // Shopify may return null with HTTP 200. Recover with the single-section
      // endpoint, then fresh page HTML from the same theme. Never replay a mutation.
      for (const mode of ['sections', 'section_id', 'page']) {
        if (destroyed) return;
        const url = new URL(renderUrl);
        if (mode === 'page') url.searchParams.set('_sca_cart', Date.now().toString());
        else url.searchParams.set(mode, sectionId);
        try {
          const response = await fetch(url, {
            credentials: 'same-origin', cache: 'no-store', signal,
            headers: { Accept: mode === 'sections' ? 'application/json' : 'text/html' }
          });
          if (!response.ok) throw new Error('Cart response unavailable');
          const html = mode === 'sections' ? (await response.json())[sectionId] : await response.text();
          if (destroyed) return;
          applySection(html);
          return;
        } catch (error) {
          if (error.name === 'AbortError') throw error;
        }
      }
      throw new Error('Le panier n’a pas pu être actualisé. Réessayez ou ouvrez la page panier.');
    }
    function enqueue(work) {
      if (destroyed) return Promise.resolve(false);
      const focused = document.activeElement;
      const rowKey = focused.closest?.('[data-line-key]')?.dataset.lineKey;
      if (drawer.open && rowKey && drawer.contains(focused)) {
        const action = focused.dataset.action || 'plus';
        restoreFocus = () => [...drawer.querySelectorAll('[data-line-key]')]
          .find(el => el.dataset.lineKey === rowKey)?.querySelector('[data-action="' + action + '"]')
          || $('[data-cart-discover]') || $('[data-quantity]');
      }
      setBusy(true);
      const task = sequence.then(async () => {
        if (destroyed) return false;
        setBusy(true);
        errorMessage('');
        try { await work(); return true; }
        catch (error) {
          if (error.name !== 'AbortError' && !destroyed) {
            stale = true;
            errorMessage(error.message || 'Impossible d’actualiser le panier. Réessayez.');
          }
          return false;
        } finally { if (!destroyed) setBusy(false); }
      });
      sequence = task.catch(() => false);
      return task;
    }
    function refresh() { return enqueue(loadSection); }
    function changeLine(key, quantity) {
      if (busy || stale || !Number.isInteger(quantity) || quantity < 0) return;
      return enqueue(async () => {
        try {
          const response = await fetch(changeUrl, {
            method: 'POST', credentials: 'same-origin', signal,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity, sections: [sectionId], sections_url: renderUrl.pathname + renderUrl.search })
          });
          const data = await response.json();
          if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : 'Cette quantité n’est pas disponible.');
          try { applySection(data.sections?.[sectionId]); }
          catch { await loadSection(); }
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          // A failed response can follow a successful mutation. Read back; never repeat the POST.
          try { await loadSection(); }
          catch { stale = true; }
          errorMessage(error.message || 'La modification n’a pas pu être confirmée. Vérifiez votre panier.');
        }
      });
    }
    function lockScroll() {
      if (scrollStyles) return;
      scrollStyles = [document.documentElement.style.overflow, document.body.style.overflow];
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    function unlockScroll() {
      if (!scrollStyles) return;
      document.documentElement.style.overflow = scrollStyles[0];
      document.body.style.overflow = scrollStyles[1];
      scrollStyles = null;
    }
    function finishClose() {
      if (drawer.open) drawer.close();
      unlockScroll();
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
      restoreFocus = null;
    }
    function close(immediate = false) {
      cancelAnimationFrame(frame);
      clearTimeout(closeTimer);
      drawer.classList.remove('is-open');
      if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) finishClose();
      else closeTimer = setTimeout(finishClose, 260);
    }
    function open() {
      if (destroyed) return Promise.resolve(false);
      if (typeof drawer.showModal !== 'function') { location.assign(cartUrl); return Promise.resolve(false); }
      clearTimeout(closeTimer);
      if (!drawer.open) {
        returnFocus = document.activeElement;
        drawer.showModal();
        lockScroll();
      }
      frame = requestAnimationFrame(() => {
        if (!drawer.open) return;
        drawer.classList.add('is-open');
        $('.sca-drawer__close').focus({ preventScroll: true });
      });
      return refresh();
    }
    on(drawer, 'cancel', event => { event.preventDefault(); close(); });
    on(drawer, 'keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = [...drawer.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]')]
        .filter(el => el.getClientRects().length > 0);
      const first = controls[0];
      const last = controls.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    });
    on(drawer, 'close', () => { drawer.classList.remove('is-open'); unlockScroll(); });
    on(drawer, 'click', event => {
      if (event.target.closest('[data-sca-drawer-close]')) { close(); return; }
      if (event.target.closest('[data-cart-retry]')) { refresh(); return; }
      const button = event.target.closest('[data-quantity]');
      if (button && !button.disabled) changeLine(button.closest('[data-line-key]').dataset.lineKey, Number(button.dataset.quantity));
      const discover = event.target.closest('[data-cart-discover]');
      if (discover) {
        const destination = new URL(discover.href, location.href);
        if (destination.origin === location.origin && destination.pathname === location.pathname && destination.hash) close(true);
      }
    });
    on(drawer, 'submit', event => { if (busy || stale) event.preventDefault(); });
    on(document, 'click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest('a, [data-sca-cart-open]');
      if (!trigger || trigger.closest('[data-sca-drawer]') || trigger.hasAttribute('download') || (trigger.target && trigger.target !== '_self')) return;
      const url = trigger.href ? new URL(trigger.href, location.href) : null;
      const isCart = url && url.origin === location.origin && url.pathname.replace(/\/$/, '') === cartUrl.replace(/\/$/, '') && !url.search;
      if (!isCart && !trigger.hasAttribute('data-sca-cart-open')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open();
    }, { capture: true });
    on(document, 'sca:cart:added', () => { open(); });
    on(document, 'cart:updated', () => { if (drawer.open) refresh(); });
    updateCount(Number($('[data-cart-content]').dataset.count) || 0);
    return {
      drawer, open, close, refresh,
      destroy() { destroyed = true; close(true); lifecycle.abort(); }
    };
  }
  function mount() {
    const drawer = document.querySelector('dialog[data-sca-drawer]');
    if (instance?.drawer === drawer) return;
    instance?.destroy();
    instance = drawer ? createDrawer(drawer) : null;
    if (instance) window.SCACart = { open: instance.open, close: instance.close, refresh: instance.refresh };
    else delete window.SCACart;
  }
  window.__scaCartDrawerManager = { mount };
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    if (instance && event.target.contains(instance.drawer)) {
      instance.destroy(); instance = null; delete window.SCACart;
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
